import { useEffect, useState } from 'react'
import SettingsStyles from '../SettingsModal.module.scss'
import Styles from './Properties.module.scss'
import { Modal } from '../../../common/Modal/Modal'
import { PropertyForm } from '../../../common/PropertyForm/PropertyForm'
import { PropertiesForForms, Property, PropertyClient, PropertyType } from '../../../api/clients/PropertyClient'
import { usePropertiesList } from '../../../common/PropertiesList/usePropertiesList'
import { useQueryClient } from 'react-query'
import { showToastMessage } from '../../../common/ToastMessage/ShowToastMessage'
import { PropertiesTable } from './PropertiesTable'
import { Workspace } from '../../../types'
import { ConfirmModal } from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/Properties/ConfirmModal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Modal as AlmanacUiModal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

interface Props {
  workspace: Workspace
}

const Properties = ({ workspace }: Props) => {
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const queryClient = useQueryClient()
  const [formProperty, setFormProperty] = useState<Partial<Property> | undefined>(undefined)
  const [type, setType] = useState<PropertyType>(PropertyType.SingleSelect)
  const { isMobile } = useResponsiveDesign()

  const { openModal, closeModal } = useModals()

  useEffect(() => {
    if (formProperty?.type) {
      setType(formProperty.type)
    }
  }, [formProperty?.type])

  const { createProperty, updateProperty, deleteProperty } = usePropertiesList({
    workspaceId: workspace.key,
  })

  const deleteWorkspaceProperty = async (property: Property) => {
    await deleteProperty(property)
    await queryClient.invalidateQueries(['workspace', workspace.key, 'properties'])
    showToastMessage(`"${property.name}" has been deleted.`)
    setFormProperty(undefined)
    closeModal()
  }

  const onRemoveHandler = async (property: Property) => {
    openModal(ConfirmModal, {
      onConfirm: () => deleteWorkspaceProperty(property),
    })
  }

  const onUpdateHandler = async (property: Property, isUndoAction: boolean | undefined) => {
    const res = await updateProperty(property, isUndoAction)
    await queryClient.invalidateQueries(['workspace', workspace.key, 'properties'])
    setFormProperty(undefined)
    showToastMessage(`"${property.name}" has been updated.`)
    return res
  }

  const onCreateHandler = async (property: Property) => {
    const res = await createProperty(property)
    await queryClient.invalidateQueries(['workspace', workspace.key, 'properties'])
    setFormProperty(undefined)
    showToastMessage(`"${property.name}" has been created.`)
    return res
  }

  const propertyTypes = Object.keys(PropertyType)
    .map((key) => ({
      is_new: true,
      type: PropertyType[key as keyof typeof PropertyType],
      name: String(PropertyType[key as keyof typeof PropertyType]),
    }))
    .filter(({ type }) => PropertiesForForms.includes(type))

  return (
    <>
      {!isMobile && !isSettingsModalUpdateEnabled && (
        <div className={Styles.headerContainer}>
          <div className={SettingsStyles.subHeader}>
            <Heading level={3}>Properties</Heading>
          </div>
        </div>
      )}
      {!isMobile && isSettingsModalUpdateEnabled && (
        <AlmanacUiModal.Header title="Properties" className={SettingsStyles.newHeader} />
      )}

      <div className={Styles.fixedContent}>
        <div className={Styles.subtitleWrapper}>
          <Heading level={5}>Workspace properties</Heading>
        </div>
        <div className={Styles.propertiesText}>
          <Text variant="secondary" color="var(--dark-60)">
            Admins can create workspace properties, like "Team", "Project", or "Due Date", for consistent organization.
            Assign and manage these properties in the Properties section of docs and folders.
          </Text>
        </div>
      </div>

      <PropertiesTable
        onEditButtonClick={(property) => {
          setFormProperty(property)
          setType(property.type)
        }}
        onToggleDefaultClick={async (property) => {
          if (property.default_for_articles) {
            await PropertyClient.updateProperty(workspace.key, {
              ...property,
              default_for_articles: false,
            })

            await queryClient.invalidateQueries(['workspace', workspace.key, 'properties'])
            showToastMessage(`"${property.name}" marked as optional.`)
          } else {
            await PropertyClient.updateProperty(workspace.key, {
              ...property,
              default_for_articles: true,
            })

            await queryClient.invalidateQueries(['workspace', workspace.key, 'properties'])
            showToastMessage(`"${property.name}" marked as default.`)
          }
        }}
        onDeleteProperty={onRemoveHandler}
        setType={setType}
        setFormProperty={setFormProperty}
      />

      <Modal
        wrapperClassName={Styles.modalWrapper}
        bodyClassName={Styles.modalBody}
        show={!!formProperty}
        onClose={() => {
          setFormProperty(undefined)
        }}
        showCloseIcon={false}
      >
        <PropertyForm
          showAddToDefault
          variant={'large'}
          type={type}
          workspaceProperty
          onSetType={setType}
          propertyTypes={propertyTypes}
          property={formProperty as Property | undefined}
          removeText="Delete"
          onRemove={onRemoveHandler}
          onUpdate={onUpdateHandler}
          onCreate={onCreateHandler}
        />
      </Modal>
    </>
  )
}

export default Properties
